/**
 * Loong Fu Offline Sync v12.3.0
 * IndexedDB-backed queue for attendance/payment updates when offline.
 * Uses timestamps for conflict resolution on sync.
 */

const DB_NAME = 'loongfu_offline';
const DB_VERSION = 1;
const STORE_NAME = 'syncQueue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Queue an action for background sync */
export async function queueOfflineAction(url, method, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      url,
      method,
      data,
      timestamp: new Date().toISOString(),
      retries: 0,
    });

    // Request background sync if available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('loongfu-offline-sync');
    }
    return true;
  } catch {
    return false;
  }
}

/** Get count of pending offline actions */
export async function getPendingCount() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return new Promise((resolve) => {
      const req = store.count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(0);
    });
  } catch {
    return 0;
  }
}

/** Manual sync — process all queued items (for browsers without Background Sync) */
export async function syncNow(apiBase) {
  if (!navigator.onLine) return { synced: 0, failed: 0 };

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const items = await new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });

  let synced = 0;
  let failed = 0;

  for (const item of items) {
    try {
      const res = await fetch(item.url, {
        method: item.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...item.data, _offline_timestamp: item.timestamp }),
      });

      if (res.ok) {
        const delTx = db.transaction(STORE_NAME, 'readwrite');
        delTx.objectStore(STORE_NAME).delete(item.id);
        synced++;
      } else {
        failed++;
      }
    } catch {
      failed++;
      break; // Still offline
    }
  }

  return { synced, failed };
}

/** Check online status */
export function isOnline() {
  return navigator.onLine;
}
